<template>
  <div :class="[_class, getService().code]">
    <cat-skip-link
      :link-text="resolveTxt('skin.skip.to.content', 'Skip to main content')"
      link="#maincontent"
    ></cat-skip-link>
    <cat-release-info v-if="viewtree.releaseStatus" class-name="beta-info">
      <div class="beta-info-box">
        <div class="beta-info-box__header mbs">
          <cat-pill level="info" value="RELEASE INFORMATION"></cat-pill>
        </div>
        <div class="mlx">
          <p class="text-size-small">
            This service is currently undergoing an upgrade.
          </p>
        </div>
      </div>
    </cat-release-info>

    <cat-background />

    <cat-header
      v-if="!noHeader && !getService().hideHeaderFooter"
      :logo-disabled="!getService().displayMenus"
    >
      <template #header-controls>
        <cat-account
          :user="user"
          :organisation-identifier="organisationIdentifier"
          :display-menus="getService().displayMenus"
          :confirm-exit-service="confirmExitService"
          @ui-change-organisation="changeOrgHandler"
          @ui-logoff="onExitService"
        ></cat-account>
      </template>
    </cat-header>

    <div
      v-if="getService().displayMenus"
      class="nav-bar filled invert-text pattern menu-display"
    >
      <div class="grid-container">
        <div class="grid-x ptn pbn">
          <slot name="mainmenu"></slot>
        </div>
      </div>
    </div>

    <div id="maincontent" role="main" :class="additionalClasses">
      <div
        v-if="getService().displayTitle && !getService().displayMenus"
        class="nav-bar filled invert-text pattern title-display menu-included"
      >
        <div class="grid-container">
          <div class="grid-x">
            <div class="small-12 cell relative">
              <div class="header-group">
                <component
                  :is="headingComponent"
                  data-label-type="service-title"
                  >{{ getService().name }}</component
                >
                <span
                  v-if="serviceTitleNote && isCorrectMode"
                  class="service-title-note"
                  >{{ serviceTitleNote }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="grid-container">
        <div
          v-if="getService().displayTitle && getService().displayMenus"
          class="grid-x grid-margin-x mts pbn menu-title-included"
        >
          <div class="small-12 cell">
            <div class="header-group">
              <component
                :is="headingComponent"
                data-label-type="service-title"
                class="mbs"
              >
                {{ getService().name }}
              </component>
            </div>
          </div>
        </div>
      </div>
      <div class="grid-container">
        <div class="grid-x grid-margin-x ptn">
          <div class="small-12 cell">
            <slot name="main_content"></slot>
          </div>
        </div>
      </div>
    </div>
    <cat-page-help
      :url="helpUrl"
      :open="helpDrawer"
      :source-id="helpSourceId"
      :title="helpSourceTitle"
      :section-help="sectionHelp"
      :show-tooltip="true"
      @ui-close="closeHelp(viewtree.helpUrl)"
    />

    <cat-page-scroll-to-top
      v-if="getService().scrollToTopDistanceFromTop >= 0"
      :aria-label="
        resolveTxt(
          'skin.scroll.to.top.aria-label',
          'Scroll to the top of the page'
        )
      "
      :distance-from-top="getService().scrollToTopDistanceFromTop"
      :buttonText="resolveTxt('skin.scroll.to.top', 'Scroll to top')"
    />
    <cat-footer
      v-if="!getService().hideHeaderFooter"
      :content="footerContent"
      class="footerFrame"
    />
    <confirm-dialog
      :id="getService().code + '-leave-dialog'"
      :target="serviceConfirmDialogTarget"
      :heading="serviceConfirmDialogDetails.title"
      :msg="serviceConfirmDialogDetails.text"
      :cancel-label="serviceConfirmDialogDetails.cancel"
      :confirm-label="serviceConfirmDialogDetails.confirm"
      :top-close-button="false"
      size="medium"
      @ui-confirm="confirmExitServiceConfirmDialog"
      @ui-cancel="cancelExitServiceConfirmDialog"
    >
      <template #button-confirm>
        <cat-btn
          :primary="true"
          :url="serviceConfirmDialogTarget && serviceConfirmDialogTarget.url"
          :label="serviceConfirmDialogDetails.confirm"
          @ui-click="exitService()"
        ></cat-btn>
      </template>
    </confirm-dialog>
    <cat-throbber />
  </div>
</template>

<script lang="ts">
import { defineComponent, provide, inject, ref } from "vue";
import CatSkipLink from "@/components/catSkipLink/catSkipLink.vue";
import CatAccount from "../catAccount/catAccount.vue";
import CatBtn from "@/components/catBtn/catBtn.vue";
import CatFooter from "./footer.vue";
import CatThrobber from "./throbber.vue";
import CatPageHelp from "./catPageHelp.vue";
import CatPageScrollToTop from "./catPageScrollToTop.vue";
import CatReleaseInfo from "./catReleaseInfo.vue";
import CatBackground from "./background-layer.vue";
import CatHeader from "./header.vue";
import CatHelpPanel from "@/components/catPageTemplate/catHelpPanel.vue";
import CatPill from "@/components/catPill/cat-pill.vue";
import PageHelpMixin from "@/components/catPageTemplate/pageHelpMixin";
import ConfirmDialog from "@/components/layouts/confirm-dialog.vue";
import { defaultStopProcessUpdate } from "@/components/utilities/processUpdateMixin";
import services from "@/components/services.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", services.resolveTxt);
    const displayParams = inject("displayParams", services.displayParams);
    const getService = inject("getService", services.getService);
    const getMenu = inject("getMenu", services.getMenu);
    const hLevel = getService().displayTitle ? 1 : null;

    // Provide the stored value
    provide("suppressExternalHelp", getService().suppressExternalHelp);
    provide("headingLevel", hLevel);

    return {
      resolveTxt,
      displayParams,
      getService,
      getMenu,
      hLevel
    };
  },
  name: "CatServiceLayout",
  components: {
    CatSkipLink,
    CatAccount,
    CatBtn,
    CatFooter,
    CatPageHelp,
    CatPageScrollToTop,
    CatReleaseInfo,
    CatHelpPanel,
    CatThrobber,
    CatBackground,
    CatHeader,
    CatPill,
    ConfirmDialog
  },
  mixins: [PageHelpMixin],
  inject: {
    stopProcessUpdate: {
      from: "stopProcessUpdate",
      default: () => defaultStopProcessUpdate
    }
  },
  props: {
    viewtree: {
      type: Object,
      required: true
    },
    changeOrgHandler: {
      type: Function,
      required: true,
      default: () => {}
    },
    // Pass in the Catalyst API so we can replace it in unit tests
    catalystApi: {
      type: Object,
      default: window["Catalyst"]
    },
    noHeader: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      helpUrl: this.viewtree.helpUrl,
      drawer: false,
      accountDrawer: false,
      user: this.viewtree.usr,
      organisationIdentifier: this.viewtree.organisationIdentifier,
      additionalClasses: [],
      catalyst: this.catalystApi || window["Catalyst"],
      footerContent: this.viewtree.footerContent,
      serviceRedirectUrl: null,
      serviceConfirmDialogTarget: null,
      serviceConfirmDialogDetails: {}
    };
  },
  methods: {
    cancelExitServiceConfirmDialog: function () {
      this.serviceRedirectUrl = null;
      this.serviceConfirmDialogTarget = null;
    },
    confirmExitServiceConfirmDialog: function () {
      this.exitService();
    },
    onExitService: function (event) {
      this.serviceRedirectUrl = event.url;

      if (this.confirmExitService) {
        this.serviceConfirmDialogDetails.title = event.confirmTitle
          ? event.confirmTitle
          : this.resolveTxt(
              "service.dialog.confirm.leave.title",
              "Are you sure you want to leave?"
            );

        this.serviceConfirmDialogDetails.text = event.confirmText
          ? event.confirmText
          : this.resolveTxt(
              "service.dialog.confirm.leave.text",
              "You will lose your changes."
            );

        this.serviceConfirmDialogDetails.confirm = event.confirmButtonConfirm
          ? event.confirmButtonConfirm
          : this.resolveTxt("service.dialog.confirm.leave.confirm", "Leave");

        this.serviceConfirmDialogDetails.cancel = event.confirmButtonCancel
          ? event.confirmButtonCancel
          : this.resolveTxt("service.dialog.confirm.leave.cancel", "Go back");

        // Display the confirm dialog
        this.serviceConfirmDialogTarget = {
          url: event.url
        };
        this.stopProcessUpdate(
          "catServiceLayout.vue name: cat-service-layout method: onExitService"
        );
      } else {
        this.exitService();
      }
    },
    exitService: function () {
      var showLoader = new CustomEvent("ui-block", { detail: {} });
      document.dispatchEvent(showLoader);
      return false;
    },
    toggleMenu: function (evt) {
      this.drawer = !this.drawer;
    },
    addContentClass: function (classname) {
      if (this.additionalClasses.indexOf(classname) == -1) {
        this.additionalClasses.push(classname);
      }
    },
    getDisplayParams: function () {
      return {
        showLabelOptional: this.getService().showLabelOptional,
        showLabelRequired: this.getService().showLabelRequired,
        suppressOffscreenOptional: this.getService().suppressOffscreenOptional,
        suppressOffscreenRequired: this.getService().suppressOffscreenRequired,
        showOptionalOnGroupFields: this.getService().showOptionalOnGroupFields
      };
    }
  },
  computed: {
    _class: function () {
      return {
        "cat-layout-service": true,
        "app-content": true,
        "logged-in": !this.viewtree.usr.guest,
        "not-logged-in": this.viewtree.usr.guest,
        correct: this.isCorrectMode
      };
    },
    isCorrectMode: function () {
      var mode = this.getService().mode && this.getService().mode.toLowerCase();
      return mode === "correct" || mode === "correctadd";
    },
    headingComponent: function () {
      return (
        "h" +
        (this.getService().serviceTitleLevel > 6
          ? 6
          : this.getService().serviceTitleLevel)
      );
    },
    serviceTitleNote: function () {
      return this.resolveTxt("skin.correct.serviceTitleNote", "");
    },
    confirmExitService: function () {
      return this.getService().confirmExitService;
    }
  },
  mounted: function () {
    if (this.getService().contentClasses) {
      this.additionalClasses.push(this.getService().contentClasses);
    }
    if (this.getService().hideHeaderFooter) {
      document.querySelector("body").classList.add("ptn", "verne-admin");
    }
    if (this.getService().displayTitle && this.getService().hideHeaderFooter) {
      document.querySelector("html").classList.add("verne-admin-scroll");
      document.querySelector("body").classList.add("verne-admin-title");
    }
    if (
      navigator.userAgent.indexOf("MSIE") !== -1 ||
      navigator.appVersion.indexOf("Trident/") > -1
    ) {
      /* Microsoft Internet Explorer detected in. */
      document.querySelector("body").classList.add("if-ie");
    }
    this.initPageHelp(this.viewtree.helpUrl);
  },
  provide: function () {
    return {
      addContentClass: this.addContentClass,
      displayParams: this.getDisplayParams()
    };
  }
});
</script>
